import { motion } from "framer-motion";
import { Building2, Gift, Users, DollarSign, Globe, Rocket, Target, TrendingUp } from "lucide-react";

const benefits = [
  {
    icon: Building2,
    title: "Platform for Companies",
    description: "A dedicated portal where partner companies can offer homeownership as a benefit to their employees.",
  },
  {
    icon: DollarSign,
    title: "Down Payment Assistance",
    description: "Companies can contribute toward their employees' down payments, making homeownership more accessible.",
  },
  {
    icon: Users,
    title: "CityWorth Dedicated Team",
    description: "Each company gets a dedicated team of real estate and mortgage professionals to serve their employees.",
  },
  {
    icon: Gift,
    title: "Homebuying Rewards",
    description: "Significant incentives and rewards for employees who purchase homes through the CityWorth platform.",
  },
];

const COLLAB_IMAGE = "https://customer-assets.emergentagent.com/job_5ead2d97-7ad4-48d6-8ee4-b13556106f69/artifacts/er06dsvm_image.png";

const EmployeeBenefitSection = () => {
  return (
    <section className="py-24 md:py-32 relative bg-slate-50" data-testid="employee-benefit-section">
      <div className="container-main relative z-10">
        {/* Section header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="text-sm font-semibold tracking-widest uppercase text-blue-600 mb-4 block">
            Our Unique Approach
          </span>
          <h2 className="text-3xl md:text-5xl font-bold mb-6 text-slate-900">
            Thinking{" "}
            <span className="gradient-text">Bigger</span>
          </h2>
          <p className="text-lg text-slate-600 max-w-3xl mx-auto">
            While everyone else is thinking local, CityWorth is thinking statewide and nationwide. 
            We're building our business by partnering with companies to offer our <strong>Homeownership Employee Benefit</strong>—a 
            revolutionary way to find customers at scale.
          </p>
        </motion.div>

        {/* Vision Statement */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="mb-16"
        >
          <div className="bg-gradient-to-r from-red-600 to-blue-600 rounded-2xl p-8 md:p-12 text-white relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
            <div className="relative z-10 flex flex-col md:flex-row items-center gap-8">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-4">
                  <Globe className="w-8 h-8" />
                  <span className="text-lg font-semibold">Nationwide Vision</span>
                </div>
                <h3 className="text-2xl md:text-3xl font-bold mb-4">
                  A Different Way to Find Customers
                </h3>
                <p className="text-white/90 text-lg leading-relaxed">
                  CityWorth is focused on finding innovative ways to connect with homebuyers. By partnering with 
                  companies across the country, we're creating a pipeline of pre-qualified buyers who are ready 
                  to purchase—giving you access to leads that traditional agents could never reach.
                </p>
              </div>
              <div className="flex-shrink-0">
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-white/20 rounded-xl p-4 text-center">
                    <Rocket className="w-6 h-6 mx-auto mb-2" />
                    <div className="text-sm">Statewide</div>
                  </div>
                  <div className="bg-white/20 rounded-xl p-4 text-center">
                    <Target className="w-6 h-6 mx-auto mb-2" />
                    <div className="text-sm">Nationwide</div>
                  </div>
                  <div className="bg-white/20 rounded-xl p-4 text-center">
                    <TrendingUp className="w-6 h-6 mx-auto mb-2" />
                    <div className="text-sm">Scalable</div>
                  </div>
                  <div className="bg-white/20 rounded-xl p-4 text-center">
                    <Users className="w-6 h-6 mx-auto mb-2" />
                    <div className="text-sm">Enterprise</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Content with image */}
        <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
          {/* Image */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="image-card rounded-2xl overflow-hidden shadow-xl"
          >
            <img 
              src={COLLAB_IMAGE} 
              alt="CityWorth professional using technology" 
              className="w-full h-80 lg:h-96 object-cover"
            />
          </motion.div>

          {/* Benefits list */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h3 className="text-2xl font-bold text-slate-900 mb-6">
              CityWorth's Homeownership Employee Benefit
            </h3>
            <div className="space-y-4">
              {benefits.map((benefit, index) => (
                <motion.div
                  key={benefit.title}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="flex items-start gap-4 p-4 bg-white rounded-xl shadow-sm border border-slate-100 hover:shadow-md transition-shadow"
                >
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-red-500 to-blue-500 flex items-center justify-center flex-shrink-0">
                    <benefit.icon className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-1">{benefit.title}</h4>
                    <p className="text-slate-600 text-sm">{benefit.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>

        {/* Bottom message */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="text-center"
        >
          <div className="inline-flex items-center gap-3 bg-white rounded-full px-6 py-3 shadow-sm border border-slate-200">
            <Globe className="w-5 h-5 text-blue-600" />
            <span className="text-slate-700">
              This is how CityWorth provides you with <span className="font-semibold text-slate-900">pre-approved, ready-to-buy customers</span>.
            </span>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default EmployeeBenefitSection;
